/* PreNumbers Critical Finance — reskinned shell + context.

   Mirrors the Financial Builder reskin (src/components/FinancialBuilder): a
   left-sidebar app shell, a topbar, and a `goTo` helper. Data flow is unchanged
   (pageNum, entities, entitySelected/updateEntity, spotRates, messageApi).
   Page flow unchanged: 1 Entities -> 2 Projections -> 3 Dashboard. */

import { useState, createContext, useEffect, useContext } from 'react';
import { message } from 'antd';
import {
  ApartmentOutlined, LineChartOutlined, DashboardOutlined, DownOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';

import { fetchWithAccessTokenRetry } from '../helperFunctions.jsx';
import { ThemeToggle } from '../Navbar/Navbar.jsx';

import EntitiesPage from './EntitiesPage/EntitiesPage.jsx';
import ProjectionsPage from './ProjectionsPage/ProjectionsPage.jsx';
import Dashboard from './DashboardOverview/Dashboard.jsx';

/* Reuse the Financial Builder design system. Tokens come from
   styles/colors_and_type.css (imported app-wide). */
import '../FinancialBuilder/financial-builder.css';

export const CriticalFinancePageContext = createContext(null);

/* ------------------------------- shell pieces ------------------------------ */
function Sidebar() {
  const { pageNum, goTo, entitySelected, entities } = useContext(CriticalFinancePageContext);
  const hasYears = !!entitySelected && (entitySelected.years?.length > 0);
  const nav = [
    { n: 1, label: 'Entities', icon: <ApartmentOutlined />, enabled: true },
    { n: 2, label: 'Projections', icon: <LineChartOutlined />, enabled: !!entitySelected },
    { n: 3, label: 'Dashboard', icon: <DashboardOutlined />, enabled: hasYears },
  ];
  return (
    <aside className="pn-side">
      <div className="pn-brand">
        <Link to="/" className="navbar-logo">
          <img className="navbar-logo-img" src="/imgs/logo.png" alt="PreNumbers" />
          <span className="navbar-logo-text">PreNumbers</span>
        </Link>
      </div>
      <div className="pn-nav-group-label">Critical Finance</div>
      {nav.map((item) => (
        <div key={item.n}
          className={"pn-nav-item" + (pageNum === item.n ? " active" : "")}
          style={item.enabled ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          onClick={() => item.enabled && goTo(item.n)}>
          {item.icon}<span>{item.label}</span>
        </div>
      ))}
      <div className="pn-nav-spacer" />
      <div className="pn-side-card">
        <div className="lbl">Active entity</div>
        <div className="val">{entitySelected ? entitySelected.name : 'None selected'}</div>
        <div className="lbl" style={{ marginTop: 8 }}>Workspace</div>
        <div className="val">{entities.length} entities</div>
      </div>
    </aside>
  );
}

function Topbar() {
  const { pageNum, entitySelected } = useContext(CriticalFinancePageContext);
  const crumb = ['Entities', 'Projections', 'Dashboard'][pageNum - 1] || 'Entities';
  return (
    <header className="pn-topbar">
      <div className="pn-crumbs"><span>Critical Finance</span><span className="sep">/</span><span className="cur">{crumb}</span></div>
      <div className="pn-topbar-spacer" />
      {entitySelected && (
        <div className="pn-company-switch"><span className="pn-company-dot" />{entitySelected.name}<DownOutlined style={{ fontSize: 11 }} /></div>
      )}
      <ThemeToggle />
    </header>
  );
}

function PageSwitcher() {
  const { pageNum } = useContext(CriticalFinancePageContext);
  switch (pageNum) {
    case 1: return <EntitiesPage />;
    case 2: return <ProjectionsPage />;
    case 3: return <Dashboard />;
    default: return <EntitiesPage />;
  }
}

/* --------------------------------- data ----------------------------------- */
async function getAllUserEntities(setEntities, setSpotRates, messageApi) {
  messageApi.info('Loading entities...');
  try {
    const response = await fetchWithAccessTokenRetry('/api/cfi/getEntities');
    if (!response.ok) throw new Error('Failed to fetch entities');
    const data = await response.json();
    setEntities(data.entities);
    setSpotRates(data.spotRates);
    messageApi.destroy();
    messageApi.success('Entities loaded successfully');
  } catch (error) {
    console.error('Error fetching user entities:', error);
    setEntities([]);
    messageApi.destroy();
    messageApi.error('Failed to load entities');
  }
}

function CriticalFinancePage() {
  const [pageNum, setPageNum] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();

  const [entities, setEntities] = useState([]);
  // Tracks the selected entity for navigation so other pages can edit it.
  const [entitySelectedID, setEntitySelectedID] = useState("");
  const [entitySelected, setEntitySelected] = useState(null);
  const [spotRates, setSpotRates] = useState(null);

  // Atomic updater: edits to the selected entity flow back into the list.
  const updateEntity = (updater) => {
    setEntitySelected(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      setEntities(prevEntities => {
        const idx = prevEntities.findIndex(e => e.id === next.id);
        if (idx === -1) return prevEntities;
        const nextEntities = [...prevEntities];
        nextEntities[idx] = next;
        return nextEntities;
      });
      return next;
    });
  };

  const goTo = (n) => {
    setPageNum(n);
    const c = document.querySelector('.pn-canvas');
    if (c) c.scrollTop = 0;
  };

  const contextToUse = {
    pageNum,
    setPageNum,
    goTo,
    messageApi,
    entities,
    setEntities,
    entitySelectedID,
    setEntitySelectedID,
    entitySelected,
    setEntitySelected: updateEntity,
    spotRates,
  };

  useEffect(() => {
    getAllUserEntities(setEntities, setSpotRates, messageApi);
  }, [messageApi]);

  // Update the selected entity when the entitySelectedID changes
  useEffect(() => {
    if (entitySelectedID !== "-- Select a entity --") {
      const selectedEntity = entities.find(entity => entity.id === parseInt(entitySelectedID));
      setEntitySelected(selectedEntity);
    }
  }, [entitySelectedID, entities]);

  return (
    <>
      {contextHolder}
      <CriticalFinancePageContext.Provider value={contextToUse}>
        <div className="pn-app">
          <Sidebar />
          <div className="pn-main">
            <Topbar />
            <div className="pn-canvas"><PageSwitcher /></div>
          </div>
        </div>
      </CriticalFinancePageContext.Provider>
    </>
  );
}

export default CriticalFinancePage;
